import React, { useState } from "react";
import { TextStyle } from "react-native";
import { useTheme } from "react-native-paper";
import { useTranslation } from "react-i18next";
import MediaViewer from "./MediaViewer";
import { Field } from "../config/companies/config";
import { getFieldComponent } from "./fields/fieldDisplayRegistry";
import { getNestedValue } from "../utils/utils";

type FieldDisplayProps = {
  field: Field;
  value: any;
  onChange?: (fieldName: string, newValue: any) => void;
  item?: any;
  valueStyle?: TextStyle;
  showFileName?: boolean;
};

export const isEmptyDisplayValue = (field: Field, value: any) => {
  if (value === null || typeof value === "undefined") {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    if (field.type === "collectionPicker" && field.key) {
      return !getNestedValue(value, field.key);
    }

    return Object.keys(value).length === 0;
  }

  return false;
};

const FieldDisplay = ({
  field,
  value,
  onChange,
  item,
  valueStyle,
  showFileName = true,
}: FieldDisplayProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mediaViewerVisible, setMediaViewerVisible] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    uri: string;
    name?: string;
    mimeType?: string;
  } | null>(null);

  if (field.type !== "chat" && isEmptyDisplayValue(field, value)) {
    return null;
  }

  const openMediaViewer = (media: {
    uri: string;
    name?: string;
    mimeType?: string;
  }) => {
    setSelectedMedia(media);
    setMediaViewerVisible(true);
  };

  const closeMediaViewer = () => {
    setMediaViewerVisible(false);
    setSelectedMedia(null);
  };

  const renderFieldContent = () => {
    const FieldComponent = getFieldComponent(field.type);

    return (
      <FieldComponent
        field={field}
        value={value}
        onChange={onChange}
        item={item}
        valueStyle={valueStyle}
        showFileName={showFileName}
        t={t}
        theme={theme}
        openMediaViewer={openMediaViewer}
      />
    );
  };

  return (
    <>
      {renderFieldContent()}
      {selectedMedia && (
        <MediaViewer
          visible={mediaViewerVisible}
          onClose={closeMediaViewer}
          media={selectedMedia}
        />
      )}
    </>
  );
};

export default FieldDisplay;
